// src/pages/ApplicationChampionshipSeason/shared/steps/Step3Teams/Step3SeasonTeams.tsx
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Flex, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SeasonType } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';

import { SeasonFormData } from '../../schemas';
import { MAX_SEASON_TEAMS } from '../../constants';
import { WizardStepFooter } from '../../components/WizardStepFooter';
import { InfoCallout } from '../../components/InfoCallout';
import { TeamLite } from './types';
import { TeamsSearchPanel } from './TeamsSearchPanel';
import { SelectedTeamsPanel } from './SelectedTeamsPanel';
import { TeamsCounter } from './TeamsCounter';
import { GroupDistributionSection } from './GroupDistributionSection';

const PREFIX = 'pages.application_season.components.season_step3_teams';

type Step3SeasonTeamsProps = {
  onBack: () => void;
  onAdvance: () => void;
  onCancel: () => void;
  /**
   * Metadados iniciais (uuid → TeamLite). Em Edit, vem das season_teams já
   * carregadas do banco. Em Create, vazio.
   */
  initialMetadata?: Record<string, TeamLite>;
};

export const Step3SeasonTeams: FunctionComponent<Step3SeasonTeamsProps> = ({
  onBack,
  onAdvance,
  onCancel,
  initialMetadata,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useFormContext<SeasonFormData>();
  const seasonType = form.watch('season_type');
  // useWatch (em vez de form.watch) garante reatividade ao atualizar sub-paths
  // como `group_stages.${i}.team_uuids`, que é como o GroupDistributionSection
  // adiciona times nos grupos.
  const seasonTeamsRaw = useWatch({ control: form.control, name: 'season_teams' });
  const groupStagesRaw = useWatch({ control: form.control, name: 'group_stages' });
  const seasonTeams = useMemo(() => seasonTeamsRaw ?? [], [seasonTeamsRaw]);
  const groupStages = useMemo(() => groupStagesRaw ?? [], [groupStagesRaw]);

  // Cache local: uuid → TeamLite (nome/sigla/logo). Inicializado em Edit pelas
  // season_teams já carregadas; alimentado dinamicamente em Create conforme o
  // usuário marca times na busca.
  const [metadataByUuid, setMetadataByUuid] = useState<Record<string, TeamLite>>(
    () => initialMetadata ?? {},
  );

  // `initialMetadata` chega vazio na primeira render (queries do Edit ainda
  // não terminaram) e é populado depois. Como `useState(() => ...)` só roda 1x,
  // sincronizamos via efeito — preservando entradas que o usuário já tenha
  // adicionado clicando na busca (prev sobrescreve initialMetadata).
  useEffect(() => {
    if (!initialMetadata || Object.keys(initialMetadata).length === 0) return;
    setMetadataByUuid(prev => ({ ...initialMetadata, ...prev }));
  }, [initialMetadata]);

  const selectedTeamUuids = useMemo(
    () => new Set(seasonTeams.map(item => item.team_uuid)),
    [seasonTeams],
  );

  const selectedTeams: TeamLite[] = useMemo(
    () =>
      seasonTeams
        .map(item => metadataByUuid[item.team_uuid])
        .filter((team): team is TeamLite => !!team),
    [seasonTeams, metadataByUuid],
  );

  /**
   * Mapa team_uuid → label do grupo onde o time está alocado.
   *
   * Sempre que o time tem entrada no mapa, ele está alocado em um grupo — e
   * o valor (string) já vem com fallback de placeholder quando o `group_name`
   * está vazio no form. Times não alocados não entram no mapa → o consumidor
   * mostra "Sem grupo".
   */
  const groupLabelByTeamUuid = useMemo(() => {
    const map = new Map<string, string>();
    groupStages.forEach((group, index) => {
      const trimmed = group.group_name?.trim();
      const label =
        trimmed && trimmed.length > 0
          ? trimmed
          : t(`${PREFIX}.groups.placeholder_group_name`, { index: index + 1 });
      (group.team_uuids ?? []).forEach(teamUuid => map.set(teamUuid, label));
    });
    return map;
  }, [groupStages, t]);

  const allocatedCount = groupLabelByTeamUuid.size;
  const maxReached = seasonTeams.length >= MAX_SEASON_TEAMS;
  const isHybrid = seasonType === SeasonType.HYBRID;
  const unallocatedCount = isHybrid
    ? Math.max(seasonTeams.length - allocatedCount, 0)
    : 0;
  const hasUnallocatedTeams = unallocatedCount > 0;

  const handleToggleTeam = (team: {
    uuid: string;
    name: string;
    acronym?: string;
    logo_path?: string;
  }) => {
    const alreadySelected = selectedTeamUuids.has(team.uuid);

    if (alreadySelected) {
      // remove de season_teams + limpa de quaisquer grupos
      const nextSeasonTeams = seasonTeams.filter(
        item => item.team_uuid !== team.uuid,
      );
      form.setValue('season_teams', nextSeasonTeams, { shouldDirty: true });

      groupStages.forEach((group, index) => {
        if ((group.team_uuids ?? []).includes(team.uuid)) {
          form.setValue(
            `group_stages.${index}.team_uuids`,
            (group.team_uuids ?? []).filter(uuid => uuid !== team.uuid),
            { shouldDirty: true },
          );
        }
      });
      return;
    }

    if (maxReached) {
      toast({
        status: 'warning',
        title: t(`${PREFIX}.toasts.max_reached`, { max: MAX_SEASON_TEAMS }),
      });
      return;
    }

    setMetadataByUuid(prev => ({
      ...prev,
      [team.uuid]: {
        uuid: team.uuid,
        name: team.name,
        acronym: team.acronym,
        logo_path: team.logo_path,
      },
    }));
    form.setValue(
      'season_teams',
      [...seasonTeams, { team_uuid: team.uuid }],
      { shouldDirty: true },
    );
  };

  const handleRemoveSelected = (teamUuid: string) => {
    handleToggleTeam({ uuid: teamUuid, name: '' });
  };

  const handleNext = async () => {
    if (hasUnallocatedTeams) return;
    form.setValue('step', 3);
    const isValid = await form.trigger();
    if (isValid) onAdvance();
  };

  return (
    <Flex direction="column" gap={6}>
      <Text fontSize="18px" fontWeight="semibold">
        {t(`${PREFIX}.title`)}
      </Text>

      {isHybrid ? <InfoCallout>{t(`${PREFIX}.callout`)}</InfoCallout> : null}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <TeamsSearchPanel
          selectedTeamUuids={selectedTeamUuids}
          maxReached={maxReached}
          onToggle={handleToggleTeam}
        />
        <SelectedTeamsPanel
          teams={selectedTeams}
          groupLabelByTeamUuid={groupLabelByTeamUuid}
          showGroupBadge={isHybrid}
          onRemove={handleRemoveSelected}
        />
      </SimpleGrid>

      <TeamsCounter
        selectedCount={seasonTeams.length}
        allocatedCount={allocatedCount}
        showAllocation={isHybrid}
      />

      {isHybrid ? (
        <>
          <GroupDistributionSection
            selectedTeams={selectedTeams}
            metadataByUuid={metadataByUuid}
          />
          {hasUnallocatedTeams ? (
            <Flex
              align="center"
              gap={2}
              px={4}
              py={3}
              bg="red.50"
              borderWidth="1px"
              borderColor="red.200"
              borderRadius="md"
              color="red.700"
            >
              <ChakraFeatherIcon name="alert-circle" boxSize="16px" />
              <Text fontSize="13px">
                {t(`${PREFIX}.groups.unallocated_warning`, {
                  count: unallocatedCount,
                })}
              </Text>
            </Flex>
          ) : null}
        </>
      ) : null}

      <WizardStepFooter
        onBack={onBack}
        onCancel={onCancel}
        primaryLabel={t('pages.application_season.components.season_wizard.footer.review')}
        onPrimary={handleNext}
        primaryDisabled={hasUnallocatedTeams}
        primaryRightIcon={<ChakraFeatherIcon name="arrow-right" boxSize="16px" />}
      />
    </Flex>
  );
};
